/**
 * SDK utilities - Auto-generated.
 * @generated
 */

import type { SDKMethodOptions } from "./types";

type WrappedMethodResponse = { status: number; data: unknown; headers?: Headers };
type WrappedMethod = (...args: unknown[]) => Promise<WrappedMethodResponse>;

async function invokeWrappedMethod(fn: WrappedMethod, args: unknown[]) {
  return fn(...args);
}

/**
 * Build RequestInit from SDK options.
 */
function buildRequestInit(options?: SDKMethodOptions): RequestInit | undefined {
  if (!options?.entity_id && !options?.account_id && !options?.signal) return undefined;

  const init: RequestInit = {};
  if (options.entity_id || options.account_id) {
    init.headers = {
      ...(options.entity_id ? { "x-entity-id": options.entity_id } : {}),
      ...(options.account_id ? { "x-account-id": options.account_id } : {}),
    };
  }
  if (options.signal) {
    init.signal = options.signal;
  }
  return init;
}

/**
 * Extract success data type from Orval response union.
 * Orval generates: Promise<(SuccessResponse | ErrorResponse)>
 * where SuccessResponse = { data: T, status: 200|201|204 } & { headers: Headers }
 */
type ExtractSuccessData<T> = T extends { status: 200 | 201 | 204; data: infer D } ? D : never;

/**
 * Wrap a generated method to add SDK options support and unwrap response.
 * Types are preserved through TypeScript inference.
 */
export function wrapMethod<TFn extends (...args: any[]) => Promise<{ status: number; data: any; headers?: Headers }>>(
  fn: TFn,
) {
  type ResponseUnion = Awaited<ReturnType<TFn>>;
  type SuccessData = ExtractSuccessData<ResponseUnion>;

  return async (
    ...args: [...(Parameters<TFn> extends [...infer Rest, RequestInit?] ? Rest : Parameters<TFn>), SDKMethodOptions?]
  ): Promise<SuccessData> => {
    // Check if last arg is SDK options
    const lastArg = args[args.length - 1];
    const isSDKOptions =
      lastArg &&
      typeof lastArg === "object" &&
      ("entity_id" in lastArg || "account_id" in lastArg || "signal" in lastArg || Object.keys(lastArg).length === 0);

    let fnArgs: unknown[];
    let requestInit: RequestInit | undefined;

    if (isSDKOptions) {
      fnArgs = args.slice(0, -1);
      requestInit = buildRequestInit(lastArg as SDKMethodOptions);
    } else {
      fnArgs = [...args];
    }

    // Add requestInit to args
    fnArgs.push(requestInit);

    const response = await invokeWrappedMethod(fn as WrappedMethod, fnArgs);

    if (response.status >= 200 && response.status < 300) {
      return response.data as SuccessData;
    }

    const error = new Error(`API error: ${response.status}`);
    (error as any).status = response.status;
    (error as any).data = response.data;
    throw error;
  };
}

/**
 * Wrap a list method with pagination options support.
 * Preserves the original function's params type for module-specific fields.
 */
export function wrapListMethod<
  TFn extends (params?: any, options?: RequestInit) => Promise<{ status: number; data: any; headers?: Headers }>,
>(fn: TFn) {
  type ResponseUnion = Awaited<ReturnType<TFn>>;
  type SuccessData = ExtractSuccessData<ResponseUnion>;
  type OriginalParams = Parameters<TFn>[0];
  // Relax order_by to accept generic single-field or composite sort values.
  type ListParams = Omit<NonNullable<OriginalParams>, "order_by"> & { order_by?: string | string[] } & SDKMethodOptions;

  return async (options?: ListParams): Promise<SuccessData> => {
    const { entity_id, account_id, signal, ...params } = (options || {}) as ListParams;
    const requestInit = buildRequestInit({ entity_id, account_id, signal });

    const response = await fn(params, requestInit);

    if (response.status === 200) {
      return response.data as SuccessData;
    }

    const error = new Error(`API error: ${response.status}`);
    (error as any).status = response.status;
    (error as any).data = response.data;
    throw error;
  };
}
